import React from "react";

import { createColumnHelper } from "@tanstack/react-table-v9";
import type {
  ColumnDef,
  ColumnPinningState,
  RowData,
} from "@tanstack/react-table-v9";

import RowActions from "./components/RowActions";
import type { TableV2RowAction } from "./components/RowActions";
import {
  ExpandAllToggle,
  ExpandToggle,
  RowCheckbox,
  SelectAllCheckbox,
} from "./components/SelectionCells";
import SubTextCell from "./components/SubTextCell";
import type { TableV2Features } from "./features";
import { formatCellValue } from "./formatters";

// `any` matches TanStack's own column array type so columns with different value types fit in one array
export type TableV2ColumnDef<TData extends RowData> = ColumnDef<
  TableV2Features,
  TData,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
>;

export const SELECTION_COLUMN_ID = "__selection";
export const EXPANDER_COLUMN_ID = "__expander";
export const ACTIONS_COLUMN_ID = "__actions";

/** The actions column always stays pinned to the end, even under saved or caller-provided pinning */
export const pinActionsColumn = (
  pinning: ColumnPinningState | undefined,
  hasActions: boolean,
): ColumnPinningState => {
  const start = (pinning?.start ?? []).filter((id) => id !== ACTIONS_COLUMN_ID);
  const end = (pinning?.end ?? []).filter((id) => id !== ACTIONS_COLUMN_ID);
  return { start, end: hasActions ? [...end, ACTIONS_COLUMN_ID] : end };
};

/** Column helper bound to the shared table feature set */
export const createTableV2ColumnHelper = <TData extends RowData>() =>
  createColumnHelper<TableV2Features, TData>();

export const selectionColumn = <TData extends RowData>(options?: {
  disableSelectAll?: boolean;
}): TableV2ColumnDef<TData> => {
  return {
    id: SELECTION_COLUMN_ID,
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    enableResizing: false,
    enableHiding: false,
    enablePinning: false,
    enableGlobalFilter: false,
    // The shared Checkbox is absolutely positioned, so the cell can't clip overflow
    meta: { overflow: true, align: "center", fixedWidth: true },
    header: ({ table }) =>
      options?.disableSelectAll ? null : <SelectAllCheckbox table={table} />,
    cell: ({ row }) => <RowCheckbox row={row} />,
  };
};

/** Expand toggle per row. `toggleAll` adds an expand / collapse all button to the header */
export const expanderColumn = <TData extends RowData>(options?: {
  toggleAll?: boolean;
}): TableV2ColumnDef<TData> => {
  return {
    id: EXPANDER_COLUMN_ID,
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    enableResizing: false,
    enableHiding: false,
    enablePinning: false,
    enableGlobalFilter: false,
    meta: { overflow: true, align: "center", fixedWidth: true },
    header: ({ table }) =>
      options?.toggleAll ? <ExpandAllToggle table={table} /> : null,
    cell: ({ row }) => <ExpandToggle row={row} />,
  };
};

/** Kebab menu per row, pinned to the right edge */
export const actionsColumn = <TData extends RowData>(
  getItems: (row: TData) => TableV2RowAction[],
): TableV2ColumnDef<TData> => {
  return {
    id: ACTIONS_COLUMN_ID,
    size: 48,
    minSize: 48,
    maxSize: 48,
    enableSorting: false,
    enableResizing: false,
    enableHiding: false,
    enablePinning: false,
    enableGlobalFilter: false,
    meta: {
      type: "actions",
      overflow: true,
      align: "center",
      lockOrder: true,
      fixedWidth: true,
    },
    header: () => null,
    cell: ({ row }) => (
      <RowActions
        items={getItems(row.original)}
        testId={`row-actions-${row.id}`}
      />
    ),
  };
};

/** Starts every column in the list hidden; users turn them on from the column picker */
export const defaultHidden = <TColumn extends { meta?: object }>(
  columns: TColumn[],
): TColumn[] =>
  columns.map((column) => {
    return { ...column, meta: { ...column.meta, defaultHidden: true } };
  });

interface AuditFields {
  created?: string;
  created_by_name?: string | null;
  modified?: string;
  modified_by_name?: string | null;
}

/** Created, Created By, Last Modified and Modified By, hidden by default. The "by" names are computed by the API, so they can't sort */
export const auditColumns = <
  TData extends AuditFields,
>(): TableV2ColumnDef<TData>[] =>
  defaultHidden<TableV2ColumnDef<TData>>([
    {
      id: "created",
      accessorFn: (row) => row.created,
      header: "Created",
      size: 180,
      meta: { type: "datetime" },
    },
    {
      id: "created_by_name",
      accessorFn: (row) => row.created_by_name,
      header: "Created By",
      size: 160,
      enableSorting: false,
    },
    {
      id: "modified",
      accessorFn: (row) => row.modified,
      header: "Last Modified",
      size: 180,
      meta: { type: "datetime" },
    },
    {
      id: "modified_by_name",
      accessorFn: (row) => row.modified_by_name,
      header: "Modified By",
      size: 160,
      enableSorting: false,
    },
  ]);

/** "Last Modified": the date and time, with who made the change underneath. Replaces `UserRecordColumns` */
export const lastModifiedColumn = <
  TData extends Pick<AuditFields, "modified" | "modified_by_name">,
>(): TableV2ColumnDef<TData> => {
  return {
    id: "modified",
    accessorFn: (row) => row.modified,
    header: "Last Modified",
    size: 180,
    meta: { type: "datetime" },
    cell: ({ row: { original } }) =>
      original.modified ? (
        <SubTextCell
          content={formatCellValue(original.modified, { type: "datetime" })}
          subText={original.modified_by_name}
        />
      ) : (
        ""
      ),
  };
};
